/**
 * CountdownBadge — Phase 4 of readiness-scoring.
 *
 * Renders "N days to <target>" next to a framework score card when the
 * workspace has set an accreditation target (workspace_accreditation_targets).
 * Days are computed client-side against Date.now() so the card updates
 * even without a re-fetch, but the target itself is persisted.
 *
 * Tone follows time-to-target:
 *   > 60 days   → muted (informational)
 *   30–60 days  → warn (yellow)
 *   ≤ 30 days   → bad (red) — worth a nudge
 *   past due    → bad + "overdue by N days"
 *
 * Mode meaning is set by the API row (strict/soft); we surface it as a
 * suffix so a "soft" deadline reads as a milestone, not a cliff.
 */
import type { AccreditationTarget } from '../../lib/api';

function daysBetween(a: Date, b: Date): number {
  const oneDayMs = 24 * 60 * 60 * 1000;
  const aUtc = Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate());
  const bUtc = Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate());
  return Math.round((bUtc - aUtc) / oneDayMs);
}

interface Palette {
  bg: string;
  fg: string;
}

const PALETTE: Record<'muted' | 'warn' | 'bad', Palette> = {
  muted: {
    bg: 'var(--surface-alt, var(--surface))',
    fg: 'var(--text-dim)',
  },
  warn: {
    bg: 'var(--warning-bg)',
    fg: 'var(--warning-fg)',
  },
  bad: {
    bg: 'var(--danger-bg)',
    fg: 'var(--danger-fg)',
  },
};

export function CountdownBadge({ target }: { target: AccreditationTarget }) {
  const targetDate = new Date(`${target.target_date}T00:00:00Z`);
  if (Number.isNaN(targetDate.getTime())) return null;
  const now = new Date();
  const days = daysBetween(now, targetDate);
  const isPast = days < 0;

  const tone: 'muted' | 'warn' | 'bad' = isPast
    ? 'bad'
    : days <= 30
      ? 'bad'
      : days <= 60
        ? 'warn'
        : 'muted';

  const label = isPast
    ? `Overdue by ${Math.abs(days)}d`
    : days === 0
      ? 'Due today'
      : `${days}d to target`;

  const modeSuffix = target.mode === 'strict' ? 'strict' : 'soft';
  const palette = PALETTE[tone];

  return (
    <span
      title={`Target ${target.target_date} (${modeSuffix}). ${target.notes ?? ''}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '2px 8px',
        borderRadius: '999px',
        background: palette.bg,
        color: palette.fg,
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 500,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      <span style={{ opacity: 0.6, fontFamily: 'var(--font-mono)' }}>· {modeSuffix}</span>
    </span>
  );
}
